// Form compartido para subir/editar documentos (personas y obras).
// service: { getDocumentNames, uploadDocument(file, data, ownerId), updateDocument(file, data, docId) }.
// Las diferencias reales entre los dos usos se pasan por props: createLabel, showHints, strictResult.
import React, { useState, useEffect } from 'react';
import { Form, Button, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { FaSave, FaTimes, FaFileAlt } from 'react-icons/fa';
import dayjs from 'dayjs';

const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf', 'ods', 'xls', 'doc', 'docx', 'xlsx', 'odt', 'txt'];
const NEW_DOC_NAME_OPTION_VALUE = '---CREATE_NEW---';
const NEW_DOC_NAME_LABEL = 'Crear un nuevo nombre de documento...';

function DocumentUploadForm({
  service,
  ownerId,
  document = null,
  onSave,
  onCancel,
  createLabel = 'Guardar',
  showHints = false,
  strictResult = false,
}) {
  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState({
    name: document ? document.name : '',
    observations: document ? document.observations || '' : '',
    endDate: document ? dayjs(document.endDate).format('YYYY-MM-DD') : dayjs().add(1, 'year').format('YYYY-MM-DD')
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [docNameOptions, setDocNameOptions] = useState([]);
  const [loadingDocNames, setLoadingDocNames] = useState(true);
  const [isCreatingNewName, setIsCreatingNewName] = useState(false);

  useEffect(() => {
    const fetchDocNames = async () => {
      try {
        setLoadingDocNames(true);
        const names = await service.getDocumentNames();

        if (Array.isArray(names)) {
          let nameList = [];
          names.forEach(item => {
            let name = null;
            if (Array.isArray(item)) {
              name = item[1];
            } else if (typeof item === 'string') {
              name = item;
            } else if (item && typeof item === 'object' && item.name) {
              name = item.name;
            }
            if (name && typeof name === 'string' && name.trim() !== '') {
              nameList.push(name.trim());
            }
          });
          nameList = [...new Set(nameList)];
          setDocNameOptions(nameList);

          if (document && document.name && !nameList.includes(document.name)) {
            setIsCreatingNewName(true);
          }
        } else {
          console.error("La respuesta no es un array:", names);
          setDocNameOptions([]);
        }
      } catch (error) {
        console.error("Error al cargar nombres de documentos:", error);
        setDocNameOptions([]);
      } finally {
        setLoadingDocNames(false);
      }
    };
    fetchDocNames();
  }, [document, service]);

  const handleNameSelection = (e) => {
    const { value } = e.target;
    if (value === NEW_DOC_NAME_OPTION_VALUE) {
      window.alert("Verifique que el nombre que requiere no esta contemplado en la lista");
      setIsCreatingNewName(true);
      setFormData(prev => ({ ...prev, name: '' }));
    } else {
      setIsCreatingNewName(false);
      setFormData(prev => ({ ...prev, name: value }));
    }
    if (errors.name) {
      setErrors(prev => ({ ...prev, name: false }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
        setErrors(prev => ({ ...prev, file: `Formato no permitido. Permitidos: ${ALLOWED_EXTENSIONS.join(', ').toUpperCase()}` }));
        e.target.value = ''; setFile(null); return;
      }
      setFile(selectedFile);
      if (!formData.name && !isCreatingNewName) {
        setFormData(prev => ({ ...prev, name: selectedFile.name }));
      }
      if (errors.file) setErrors(prev => ({ ...prev, file: false }));
    } else {
      setFile(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'El nombre es requerido';
    if (!formData.endDate) newErrors.endDate = 'La fecha de vencimiento es requerida';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (isCreatingNewName) {
      const confirmCreation = window.confirm("Esta seguro que desea crear un nuevo nombre?");
      if (!confirmCreation) return;
    }

    try {
      setLoading(true);
      const dataToSave = { ...formData, name: formData.name.trim() };
      if (document) {
        const res = await service.updateDocument(file, dataToSave, document.id);
        if (strictResult && (!res || res.error)) {
          throw new Error(res?.error || 'Error desconocido al actualizar el documento');
        }
      } else {
        const res = await service.uploadDocument(file, dataToSave, ownerId);
        if (strictResult && (!res || res.error)) {
          throw new Error(res?.error || 'Error desconocido al subir el documento');
        }
      }
      if (onSave) onSave();
    } catch (error) {
      console.error('Error al guardar documento:', error);
      alert(`Error al guardar el documento: ${error.message || 'Error desconocido'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group className="mb-3">
        <Form.Label>{document ? 'Actualizar archivo (opcional)' : 'Archivo (opcional)'}</Form.Label>
        <Form.Control type="file" onChange={handleFileChange} isInvalid={!!errors.file} />
        <Form.Control.Feedback type="invalid">{errors.file}</Form.Control.Feedback>
        {showHints && (document ? (
          <Form.Text className="text-muted">
            <FaFileAlt className="me-1" />
            Archivo actual: <strong>{document.name}</strong>
            <br />
            Seleccione un nuevo archivo solo si desea reemplazarlo
          </Form.Text>
        ) : (
          <Form.Text className="text-muted">
            Seleccione el archivo del documento que desea subir (opcional)
          </Form.Text>
        ))}
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Nombre del documento*</Form.Label>
        {isCreatingNewName ? (
          <InputGroup>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              isInvalid={!!errors.name}
              placeholder="Escriba el nuevo nombre"
              required
              autoFocus
            />
            <Button
              variant="outline-secondary"
              onClick={() => {
                setIsCreatingNewName(false);
                setFormData(prev => ({ ...prev, name: '' }));
              }}
            >
              Cancelar
            </Button>
          </InputGroup>
        ) : (
          <Form.Select
            name="name"
            value={formData.name}
            onChange={handleNameSelection}
            isInvalid={!!errors.name}
            disabled={loadingDocNames}
            required
          >
            <option value="">{loadingDocNames ? 'Cargando opciones...' : 'Seleccione de la lista...'}</option>
            {docNameOptions.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
            <option value={NEW_DOC_NAME_OPTION_VALUE} style={{ fontStyle: 'italic', color: '#0d6efd' }}>
              {NEW_DOC_NAME_LABEL}
            </option>
          </Form.Select>
        )}
        <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Fecha de vencimiento*</Form.Label>
        <Form.Control type="date" name="endDate" value={formData.endDate} onChange={handleInputChange} isInvalid={!!errors.endDate} required />
        <Form.Control.Feedback type="invalid">{errors.endDate}</Form.Control.Feedback>
        {showHints && (
          <Form.Text className="text-muted">
            Fecha en que vence la validez del documento
          </Form.Text>
        )}
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Observaciones</Form.Label>
        <Form.Control as="textarea" rows={3} name="observations" value={formData.observations} onChange={handleInputChange} />
      </Form.Group>

      {showHints && document && (
        <Alert variant="info" className="mb-3">
          <small>
            <strong>Nota:</strong> Si no selecciona un nuevo archivo, solo se actualizarán
            el nombre, fecha de vencimiento y observaciones del documento.
          </small>
        </Alert>
      )}

      <div className="d-flex justify-content-end">
        <Button variant="secondary" onClick={onCancel} className="me-2" disabled={loading} type="button">
          <FaTimes className="me-1" /> Cancelar
        </Button>
        <Button variant="primary" type="submit" disabled={loading}>
          {loading ? <><Spinner as="span" animation="border" size="sm" className="me-1" />Guardando...</> : <><FaSave className="me-1" /> {document ? 'Actualizar' : createLabel}</>}
        </Button>
      </div>
    </Form>
  );
}

export default DocumentUploadForm;
